<template>
  <div v-transfer-dom :data-transfer="transfer">
    <transition :name="transitionNames[1]">
      <div :class="maskClasses" :style="wrapStyles" v-show="visible" v-if="showMask" @click="handleMask"></div>
    </transition>
    <div :class="wrapClasses" :style="wrapStyles" @click="handleWrapClick">
      <transition :name="transitionNames[0]" @after-leave="animationFinish">
        <div :class="classes" :style="mainStyles" v-show="visible">
          <div :class="contentClasses" ref="content" :style="contentStyles" @click="handleClickModal">
            <a :class="[prefixCls + '-close']" v-if="closable" @click="close">
              <slot name="close">
                <Icon type="close_hover (1)" :width="12" :height="12" ></Icon>
              </slot>
            </a>
            <div :class="[prefixCls + '-header']"
                 @mousedown="handleMoveStart"
                 v-if="showHead"
            ><slot name="header"><div :class="[prefixCls + '-header-inner']">{{ title }}</div></slot></div>
            <div :class="[prefixCls + '-body']"><slot></slot></div>
            <div :class="[prefixCls + '-footer']" v-if="!footerHide">
              <slot name="footer">
                <Button type="text" size="large" @click.native="cancel">{{ localeCancelText }}</Button>
                <Button type="primary" size="large" :loading="buttonLoading" @click.native="ok">{{ localeOkText }}</Button>
              </slot>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
  import Icon from './../Icon/index';
  import Button from './../Button/index';
  import TransferDom from '../../directives/transfer-dom';
  import Emitter from './../emitter';
  import ScrollbarMixins from './mixins-scrollbar';

  import { on, off } from './../dom';
  import { findComponentsDownward } from './../assist';

  import { modalIndex, modalIncrease } from './q';

  const prefixCls = 'am-modal';

  export default {
    name: 'Modal',
    mixins: [ Emitter, ScrollbarMixins ],
    components: {  Button, Icon },
    directives: { TransferDom },
    props: {
      value: {
        type: Boolean,
        default: false
      },
      closable: {
        type: Boolean,
        default: true
      },
      maskClosable: {
        type: Boolean,
        default: true
      },
      title: {
        type: String
      },
      width: {
        type: [Number, String],
        default: 520
      },
      okText: {
        type: String
      },
      cancelText: {
        type: String
      },
      loading: {
        type: Boolean,
        default: false
      },
      styles: {
        type: Object
      },
      className: {
        type: String
      },
      // for instance
      footerHide: {
        type: Boolean,
        default: false
      },
      scrollable: {
        type: Boolean,
        default: false
      },
      transitionNames: {
        type: Array,
        default () {
          return ['ease', 'fade'];
        }
      },
      transfer: {
        type: Boolean,
        default () {
          return !this.$IVIEW || this.$IVIEW.transfer === '' ? true : this.$IVIEW.transfer;
        }
      },
      fullscreen: {
        type: Boolean,
        default: false
      },
      mask: {
        type: Boolean,
        default: true
      },
      draggable: {
        type: Boolean,
        default: false
      },
      zIndex: {
        type: Number,
        default: 1000
      },
    },
    data () {
      return {
        prefixCls: prefixCls,
        wrapShow: false,
        showHead: true,
        buttonLoading: false,
        visible: this.value,
        dragData: {
          x: null,
          y: null,
          dragX: null,
          dragY: null,
          dragging: false
        },
        modalIndex: this.handleGetModalIndex(),  // for Esc close the top modal
      };
    },
    computed: {
      wrapClasses () {
        return [
          `${prefixCls}-wrap`,
          {
            [`${prefixCls}-hidden`]: !this.wrapShow,
            [`${this.className}`]: !!this.className,
            [`${prefixCls}-no-mask`]: !this.showMask
          }
        ];
      },
      wrapStyles () {
        return {
          zIndex: this.modalIndex + this.zIndex
        };
      },
      maskClasses () {
        return `${prefixCls}-mask`;
      },
      classes () {
        return [
          `${prefixCls}`,
          {
            [`${prefixCls}-fullscreen`]: this.fullscreen,
            [`${prefixCls}-fullscreen-no-header`]: this.fullscreen && !this.showHead,
            [`${prefixCls}-fullscreen-no-footer`]: this.fullscreen && this.footerHide
          }
        ];
      },
      contentClasses () {
        return [
          `${prefixCls}-content`,
          {
            [`${prefixCls}-content-no-mask`]: !this.showMask,
            [`${prefixCls}-content-drag`]: this.draggable,
            [`${prefixCls}-content-dragging`]: this.draggable && this.dragData.dragging
          }
        ];
      },
      mainStyles () {
        let style = {};

        const width = parseInt(this.width);
        const styleWidth = this.dragData.x !== null ? {
          top: 0
        } : {
          width: width <= 100 ? `${width}%` : `${width}px`
        };

        const customStyle = this.styles ? this.styles : {};

        Object.assign(style, styleWidth, customStyle);

        return style;
      },
      contentStyles () {
        let style = {};

        if (this.draggable) {
          if (this.dragData.x !== null) style.left = `${this.dragData.x}px`;
          if (this.dragData.y !== null) style.top = `${this.dragData.y}px`;
          const width = parseInt(this.width);
          const styleWidth = {
            width: width <= 100 ? `${width}%` : `${width}px`
          };

          Object.assign(style, styleWidth);
        }

        return style;
      },
      localeOkText () {
        if (this.okText === undefined) {
          return 'ok'
        } else {
          return this.okText;
        }
      },
      localeCancelText () {
        if (this.cancelText === undefined) {
          return 'cancel'
        } else {
          return this.cancelText;
        }
      },
      showMask () {
        return this.draggable ? false : this.mask;
      }
    },
    methods: {
      close () {
        this.visible = false;
        this.$emit('input', false);
        this.$emit('on-cancel');
      },
      handleMask () {
        if (this.maskClosable && this.showMask) {
          this.close();
        }
      },
      handleWrapClick (event) {
        // use indexOf,do not use === ,because ivu-modal-wrap can have other custom className
        const className = event.target.getAttribute('class');
        if (className && className.indexOf(`${prefixCls}-wrap`) > -1) this.handleMask();
      },
      cancel () {
        this.close();
      },
      ok () {
        if (this.loading) {
          this.buttonLoading = true;
        } else {
          this.visible = false;
          this.$emit('input', false);
        }
        this.$emit('on-ok');
      },
      EscClose (e) {
        if (this.visible && this.closable) {
          if (e.keyCode === 27) {
            const $Modals = findComponentsDownward(this.$root, 'Modal').filter(item => item.$data.visible && item.$props.closable);

            const $TopModal = $Modals.sort((a, b) => {
              return a.$data.modalIndex < b.$data.modalIndex ? 1 : -1;
            })[0];

            setTimeout(() => {
              $TopModal.close();
            }, 0);
          }
        }
      },
      animationFinish() {
        this.$emit('on-hidden');
      },
      handleMoveStart (event) {
        if (!this.draggable) return false;

        const $content = this.$refs.content;
        const rect = $content.getBoundingClientRect();
        this.dragData.x = rect.x;
        this.dragData.y = rect.y;

        const distance = {
          x: event.clientX,
          y: event.clientY
        };

        this.dragData.dragX = distance.x;
        this.dragData.dragY = distance.y;

        this.dragData.dragging = true;

        on(window, 'mousemove', this.handleMoveMove);
        on(window, 'mouseup', this.handleMoveEnd);
      },
      handleMoveMove (event) {
        if (!this.dragData.dragging) return false;

        const distance = {
          x: event.clientX,
          y: event.clientY
        };

        const diff_distance = {
          x: distance.x - this.dragData.dragX,
          y: distance.y - this.dragData.dragY
        };

        this.dragData.x += diff_distance.x;
        this.dragData.y += diff_distance.y;

        this.dragData.dragX = distance.x;
        this.dragData.dragY = distance.y;
      },
      handleMoveEnd () {
        this.dragData.dragging = false;
        off(window, 'mousemove', this.handleMoveMove);
        off(window, 'mouseup', this.handleMoveEnd);
      },
      handleGetModalIndex () {
        modalIncrease();
        return modalIndex;
      },
      handleClickModal () {
        // this.modalIndex = this.handleGetModalIndex();
      }
    },
    mounted () {
      if (this.visible) {
        this.wrapShow = true;
      }

      let showHead = true;

      if (this.$slots.header === undefined && !this.title) {
        showHead = false;
      }

      this.showHead = showHead;

      // ESC close
      document.addEventListener('keydown', this.EscClose);
    },
    beforeDestroy () {
      document.removeEventListener('keydown', this.EscClose);
      this.removeScrollEffect();
    },
    watch: {
      value (val) {
        this.visible = val;
      },
      visible (val) {
        if (val === false) {
          this.buttonLoading = false;
          this.timer = setTimeout(() => {
            this.wrapShow = false;
            this.removeScrollEffect();
          }, 300);
        } else {
          this.modalIndex = this.handleGetModalIndex();

          if (this.timer) clearTimeout(this.timer);
          this.wrapShow = true;
          if (!this.scrollable) {
            this.addScrollEffect();
          }
        }
        this.broadcast('Table', 'on-visible-change', val);
        this.broadcast('Slider', 'on-visible-change', val);  // #2852
        this.$emit('on-visible-change', val);
      },
      loading (val) {
        if (!val) {
          this.buttonLoading = false;
        }
      },
      scrollable (val) {
        if (!val) {
          this.addScrollEffect();
        } else {
          this.removeScrollEffect();
        }
      },
      title (val) {
        if (this.$slots.header === undefined) {
          this.showHead = !!val;
        }
      }
    }
  };
</script>
<style lang="less">
  @css-prefix: "am-";
  @modal-prefix-cls: ~"@{css-prefix}modal";
  @confirm-prefix-cls: ~"@{css-prefix}modal-confirm";
  @icon-prefix-cls: ~"@{css-prefix}icon";
  // Base
  @body-background        : #fff;
  @font-family            : "Helvetica Neue",Helvetica,"PingFang SC","Hiragino Sans GB","Microsoft YaHei","微软雅黑",Arial,sans-serif;
  @code-family            : Consolas,Menlo,Courier,monospace;@title-color            : #17233d;

  @text-color             : #333;
  @font-size-base         : 14px;
  @font-size-small        : 12px;
  @font-size-large        : 16px;
  @line-height-base       : 1.5;
  @line-height-computed   : floor((@font-size-base * @line-height-base));
  @border-radius-base     : 6px;
  @cursor-disabled        : not-allowed;
  // Border color
  @border-color-base      : #dcdee2;  // outside
  @border-color-split     : #e8eaec;  // inside
  @border-width-base      : 1px;            // width of the border for a component
  @border-style-base      : solid;          // style of a components border
  // Color
  @primary-color          : #2d8cf0;
  @info-color             : #2db7f5;
  @success-color          : #19be6b;
  @processing-color       : @primary-color;
  @warning-color          : #ff9900;
  @error-color            : #ed4014;
  @normal-color           : #e6ebf1;
  @link-color             : #2D8cF0;
  @link-hover-color       : tint(@link-color, 20%);
  @link-active-color      : shade(@link-color, 5%);
  @selected-color         : fade(@primary-color, 90%);
  @tooltip-color          : #fff;
  @subsidiary-color       : #808695;
  @rate-star-color        : #f5a623;
  /*Z-index*/
  @zindex-modal           : 1000;
  // Animation
  @animation-time         : .3s;
  @transition-time        : .2s;
  @ease-in-out            : ease-in-out;
  // Legend
  @legend-color           : #999;
  .@{modal-prefix-cls} {
    width: auto;
    margin: 0 auto;
    position: relative;
    outline: none;
    top: 100px;

    &-hidden {
      display: none !important;
    }

    &-wrap {
      position: fixed;
      overflow: auto;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: @zindex-modal;
      -webkit-overflow-scrolling: touch;
      outline: 0;
    }

    &-wrap * {
      box-sizing: border-box;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }

    &-mask {
      .mask;
    }

    &-content {
      position: relative;
      background-color: #fff;
      border: 0;
      border-radius: @border-radius-base;
      background-clip: padding-box;
      box-shadow: 0 4px 12px rgba(0,0,0,.15);

      &-no-mask{
        pointer-events: auto;
      }
      &-drag{
        position: absolute;
        .@{modal-prefix-cls}-header{
          cursor: move;
        }
      }
      &-dragging{
        -webkit-user-select: none;
        -moz-user-select: none;
        user-select: none;
      }
    }

    &-header {
      .content-header;
    }

    &-close {
      z-index: 1;
      .content-close(1px, 31px);
    }

    &-body {
      padding: 16px;
      font-size: 12px;
      line-height: 1.5;
    }

    &-footer {
      border-top: 1px solid @border-color-split;
      padding: 12px 18px 12px 18px;
      text-align: right;
      button + button {
        margin-left: 8px;
        margin-bottom: 0;
      }
    }

    &-fullscreen{
      width: 100% !important;
      top: 0;
      bottom: 0;
      position: absolute;

      @modal-header-height: 51px;
      @modal-footer-height: 61px;

      .@{modal-prefix-cls}-content{
        width: 100%;
        border-radius: 0;
        position: absolute;
        top: 0;
        bottom: 0;
      }

      .@{modal-prefix-cls}-body{
        width: 100%;
        overflow: auto;
        position: absolute;
        top: @modal-header-height;
        bottom: @modal-footer-height;
      }
      &-no-header .@{modal-prefix-cls}-body{
        top: 0;
      }
      &-no-footer .@{modal-prefix-cls}-body{
        bottom: 0;
      }
      .@{modal-prefix-cls}-footer{
        position: absolute;
        width: 100%;
        bottom: 0;
      }
    }
    &-no-mask{
      pointer-events: none;
    }
  }

  @media (max-width: 768px) {
    .@{modal-prefix-cls} {
      width: auto !important;
      margin: 10px;
    }
    .vertical-center-modal {
      .@{modal-prefix-cls} {
        flex: 1;
      }
    }
  }

  .@{confirm-prefix-cls} {
    padding: 0 4px;
    &-head {
      padding: 0 12px 0 0;
      &-icon {
        display: inline-block;
        font-size: 28px;
        vertical-align: middle;
        position: relative;
        top: -2px;

        &-info {
          color: @primary-color;
        }
        &-success {
          color: @success-color;
        }
        &-warning {
          color: @warning-color;
        }
        &-error {
          color: @error-color;
        }
        &-confirm {
          color: @warning-color;
        }
      }

      &-title {
        display: inline-block;
        vertical-align: middle;
        /*margin-left: 12px;*/
        font-size: @font-size-large;
        color: @title-color;
        font-weight: 700;
      }
    }

    &-body{
      padding: 10px;
      /*padding-left: 42px;*/
      font-size: @font-size-base;
      color: @text-color;
      position: relative;

      &-render{
        margin: 0;
        padding: 0;
      }
    }

    &-footer{
      margin-top: 20px;
      text-align: right;

      button + button {
        margin-left: 8px;
        margin-bottom: 0;
      }
    }
  }
  .mask() {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(55, 55, 55, 0.6);
    height: 100%;
    z-index: @zindex-modal;

    &-hidden {
      display: none;
    }
  }
  .content-header() {
    border-bottom: 1px solid @border-color-split;
    padding: 14px 16px;
    line-height: 1;

    p,
    &-inner
    {
      display: inline-block;
      width: 100%;
      height: 20px;
      line-height: 20px;
      font-size: @font-size-base;
      color: @title-color;
      font-weight: bold;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    p i, p span{
      vertical-align: middle;
    }
  }

  .content-close(@top: 0, @icon-font-size: 22px) {
    font-size: @font-size-small;
    position: absolute;
    right: 8px;
    top: 8px;
    overflow: hidden;
    cursor: pointer;

    .@{icon-prefix-cls}-ios-close {
      .close-base(@top, @icon-font-size);
    }
  }
  .close-base(@top: 0, @icon-font-size: 22px) {
    font-size: @icon-font-size;
    color: @legend-color;
    transition: color @transition-time ease;
    position: relative;
    top: @top;
    &:hover {
      color: #444;
    }
  }
</style>



// WEBPACK FOOTER //
// components/Modal/Modal.vue